import type { TFunction } from "i18next";
import type { Reminder } from "@/lib/humhub-api/types";
import { formatReminderText } from "@/lib/utils/eventUtils";

export const REMINDER_CONSTRAINTS = {
  MAX_REMINDERS: 2,
  MIN_REMINDERS: 0,
} as const;

export interface ReminderValidationResult {
  isValid: boolean;
  error?: string;
  duplicateWith?: string;
}

/**
 * Check if a reminder is duplicate
 */
export const checkDuplicateReminder = (
  newReminder: { unit: number; value: number },
  existingReminders: Reminder[],
  excludeId?: number | null,
  t?: TFunction,
): ReminderValidationResult => {
  const duplicate = existingReminders.find(
    (reminder) =>
      reminder.id !== excludeId &&
      reminder.unit === newReminder.unit &&
      reminder.value === newReminder.value,
  );

  if (duplicate) {
    const duplicateText = t
      ? formatReminderText(duplicate, t)
      : `${duplicate.value} ${duplicate.unit}`;

    return {
      isValid: false,
      error: t
        ? t("eventManage.reminderDuplicateError", { time: duplicateText })
        : `Duplicate reminder: ${duplicateText}`,
      duplicateWith: duplicateText,
    };
  }

  return { isValid: true };
};

/**
 * Check if reminder limit is reached
 */
export const checkReminderLimit = (
  currentReminders: Reminder[],
  t?: TFunction,
): ReminderValidationResult => {
  if (currentReminders.length >= REMINDER_CONSTRAINTS.MAX_REMINDERS) {
    return {
      isValid: false,
      error: t
        ? t("eventManage.reminderLimitExceeded", {
            max: REMINDER_CONSTRAINTS.MAX_REMINDERS,
          })
        : `Maximum ${REMINDER_CONSTRAINTS.MAX_REMINDERS} reminders allowed`,
    };
  }

  return { isValid: true };
};
